import React from 'react';
import type { ValidateEntry, ValidateLookup } from '../services/api';
import './ValidateEntries.css';

const PACKING_GROUP_ORDER = ['I', 'II', 'III'];

// "IMDG_42" is how Validate keys the regulation; people read it as "IMDG 42"
function regulationLabel(regulation: string): string {
  return regulation.replace(/_/g, ' ');
}

// Proper shipping names carry their scope in a trailing parenthetical ("PAINT
// (including paint, lacquer, enamel, …)"), which is what turns a list of them into
// a wall of text. The name leads; the scope is shown muted and clipped.
function splitName(properShippingName: string): { name: string; scope: string | null } {
  const match = /^(.*?)\s*\((.*)\)\s*$/.exec(properShippingName);
  return match ? { name: match[1], scope: match[2] } : { name: properShippingName, scope: null };
}

// Variants mostly share one proper shipping name and class and differ only by
// packing group, so they collapse into one row with a chip per variant.
function groupByName(entries: ValidateEntry[]): ValidateEntry[][] {
  const groups = new Map<string, ValidateEntry[]>();
  entries.forEach((entry) => {
    const key = `${entry.properShippingName}|${entry.classes.join('/')}`;
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  });
  return Array.from(groups.values());
}

function byPackingGroup(a: ValidateEntry, b: ValidateEntry): number {
  return PACKING_GROUP_ORDER.indexOf(a.packingGroup ?? '') - PACKING_GROUP_ORDER.indexOf(b.packingGroup ?? '');
}

// IMDG label colours, so the diamond reads like the placard. Classes 6 and 9 have
// white labels, which is the fallback.
const PLACARD_TONE_BY_CLASS: Record<string, string> = {
  '1': 'orange', '2': 'green', '3': 'red', '4': 'red', '5': 'yellow', '7': 'yellow', '8': 'black'
};

// Divisions whose label is not their class's colour: flammable gas is red where
// the rest of class 2 is green, and dangerous-when-wet is blue within class 4
const PLACARD_TONE_BY_DIVISION: Record<string, string> = { '2.1': 'red', '4.3': 'blue' };

function hazardTone(hazardClass: string): string {
  return PLACARD_TONE_BY_DIVISION[hazardClass]
    ?? PLACARD_TONE_BY_CLASS[hazardClass.split('.')[0]]
    ?? 'white';
}

// A compact row stands in for a secondary prediction, so it gets one name. The full
// panel stops at three to stay shorter than the result card; any further names are
// behind the "+N more" link to Validate's search.
const NAMES_SHOWN_COMPACT = 1;
const NAMES_SHOWN_FULL = 3;

const HazardDiamonds: React.FC<{ classes: string[] }> = ({ classes }) => (
  <span className="hazard-diamonds" aria-label={`Class ${classes.join(', subsidiary ')}`}>
    {classes.map((hazardClass, index) => (
      <span
        key={hazardClass}
        className={`hazard-diamond tone-${hazardTone(hazardClass)} ${index > 0 ? 'subsidiary' : ''}`}
        title={index === 0 ? `Class ${hazardClass}` : `Subsidiary hazard ${hazardClass}`}
      >
        <span>{hazardClass}</span>
      </span>
    ))}
  </span>
);

const VariantChips: React.FC<{ entries: ValidateEntry[] }> = ({ entries }) => {
  const withPackingGroup = entries.filter((entry) => entry.packingGroup !== null).sort(byPackingGroup);
  if (withPackingGroup.length > 0) {
    return (
      <span className="pg-chips">
        <span className="pg-label">PG</span>
        {withPackingGroup.map((entry) => (
          <a
            key={entry.substanceId}
            className="pg-chip"
            href={entry.url}
            target="_blank"
            rel="noreferrer"
            title={entry.variant ?? `Packing group ${entry.packingGroup}`}
          >
            {entry.packingGroup}
          </a>
        ))}
      </span>
    );
  }

  if (entries.length < 2) return null;

  return (
    <span className="pg-chips">
      {entries.map((entry, index) => (
        <a
          key={entry.substanceId}
          className="pg-chip"
          href={entry.url}
          target="_blank"
          rel="noreferrer"
          title={entry.variant ?? undefined}
        >
          {index + 1}
        </a>
      ))}
    </span>
  );
};

const EntryRow: React.FC<{ entries: ValidateEntry[]; compact: boolean }> = ({ entries, compact }) => {
  const [first] = entries;
  const { name, scope } = splitName(first.properShippingName);

  return (
    <div className="validate-row">
      <HazardDiamonds classes={first.classes} />
      <div className="validate-text">
        <a className="validate-name" href={first.url} target="_blank" rel="noreferrer" title={first.properShippingName}>
          {name}
        </a>
        {!compact && scope && <div className="validate-scope" title={scope}>{scope}</div>}
      </div>
      <VariantChips entries={entries} />
    </div>
  );
};

/**
 * What Hazcheck Validate holds for a predicted UN number. `undefined` renders
 * nothing (the backend only looks up the top predictions); `null` means Validate
 * could not be reached, which is said rather than hidden so an empty panel is not
 * mistaken for "no such UN number". `compact` keeps a secondary prediction to one
 * line so it does not outweigh the top one.
 */
const ValidateEntries: React.FC<{ lookup: ValidateLookup | null | undefined; compact?: boolean }> = ({
  lookup,
  compact = false
}) => {
  if (lookup === undefined) return null;

  if (lookup === null) {
    return <div className="validate-status">Validate details unavailable</div>;
  }

  if (lookup.entries.length === 0) {
    return (
      <div className="validate-status validate-missing">
        UN {lookup.unNumber} is not in {regulationLabel(lookup.regulation)}
      </div>
    );
  }

  const groups = groupByName(lookup.entries);
  const shown = groups.slice(0, compact ? NAMES_SHOWN_COMPACT : NAMES_SHOWN_FULL);
  const hidden = groups.length - shown.length;

  // A compact row's width belongs to the name, which already links to Validate, so
  // its search link appears only to point at names that were left out.
  let searchLink: string | null;
  if (compact) {
    searchLink = hidden > 0 ? `+${hidden} more` : null;
  } else {
    searchLink = hidden > 0 ? `+${hidden} more in Validate ↗` : 'View in Validate ↗';
  }

  return (
    <div className={`validate-panel ${compact ? 'compact' : ''}`}>
      {shown.map((group) => (
        <EntryRow key={group[0].substanceId} entries={group} compact={compact} />
      ))}
      {searchLink && (
        <a
          className="validate-link"
          href={lookup.searchUrl}
          target="_blank"
          rel="noreferrer"
          title={`All ${regulationLabel(lookup.regulation)} entries for UN ${lookup.unNumber}`}
        >
          {searchLink}
        </a>
      )}
    </div>
  );
};

export default ValidateEntries;
